import { TextMessageTemplates } from "./TextMessageTemplates";
import {
    DoctorFollowUpNextTouch,
    FaxRoutingRule,
    PriorAuthNextTouch,
    PriorAuthStatusReason,
} from "./PriorAuthorization";
import { Speciality } from "./Speciality";
import { EmailTemplates } from "@/models/EmailTemplates";
import { RefillStatusCode } from "@/models/Prescription";
import { EntityAlertConfigurations } from "@/models/AlertConfiguration";

export interface FaxTemplateOverride {
    imageId: number;
    fileName?: string;
}

export interface AutoOutboundConfiguration {
    contactDelay: number;
    fillDateMaxAge: number;
    twilioFlow: string;
}

export class AutoOutboundConfiguration {
    constructor(obj?: AutoOutboundConfiguration) {
        this.contactDelay = 0;
        this.fillDateMaxAge = 0;
        this.twilioFlow = "";
        if (obj) {
            Object.assign(this, obj);
        }
    }
}

export interface AutoOutboundStatusConfiguration extends AutoOutboundConfiguration {
    status: RefillStatusCode;
    subStatus: number | null;
}

export class AutoOutboundStatusConfiguration {
    constructor(obj?: AutoOutboundStatusConfiguration) {
        this.status = RefillStatusCode.AutoRefillable;
        this.subStatus = null;
        this.contactDelay = 0;
        this.fillDateMaxAge = 0;
        this.twilioFlow = "";
        if (obj) {
            Object.assign(this, obj);
        }
    }
}

export interface Configuration {
    refillTextDays: number;
    oldScriptRefillTextDays: number;
    licenseExpirationAlertDays: number;
    ordersAllowPV1Prescriptions: boolean;
    requireExplicitVerificationPV1: boolean;
    requireExplicitVerificationPV2: boolean;
    hideDispensingFields: boolean;
    autoRefillPromptStatement: string;
    autoRefillPromptStatementSpanish: string;
    autoRefillPromptStatementRussian: string;
    autoRefillPromptStatementSomali: string;
    autoRefillPromptStatementArabic: string;
    autoRefillPromptStatementChineseSimplified: string;
    autoRefillPromptStatementVietnamese: string;
    autoRefillPromptStatementFarsi: string;
    autoRefillPromptStatementKorean: string;
    autoRefillPromptStatementRomanian: string;
    autoRefillPromptStatementSwahili: string;
    autoRefillPromptStatementBurmese: string;
    autoRefillPromptStatementNepali: string;
    autoRefillPromptStatementAmharic: string;
    autoRefillPromptStatementPashto: string;
    autoCaseAppNewPatientCreation: boolean;
    autoCaseTextsNoValidNumbers: boolean;
    autoCaseTextsPatientContactFailure: boolean;
    autoCaseTextsFailAutoRefill: boolean;
    partialPaymentTextThresholdAmount: number;
    npcTextThresholdAmount: number;
    infinitrakApiKey: string;
    disableMedGuidePrinting: boolean;
    inventoryReasons: string[];
    textMessageTemplates: TextMessageTemplates;
    emailTemplates: EmailTemplates;
    readyForPickupTexts: boolean;
    disableWebNotifications: boolean;
    orderCancellationReasons: string[];
    requireLotsOnInventoryBatches: boolean;
    blockDeactivatingDrugIfInventoryOnHand: boolean;
    inventoryBatchSubmitEndpoint: string;
    allowAutorefillWithoutNotification: boolean;
    allowAutorefillWithoutPayment: boolean;
    onHoldSubStatuses: any[];
    discontinuedSubStatuses: any[];
    faxCoverPage: boolean;
    patientAppCheckoutAttestation: string;
    requireExplicitDur: number | null;
    requireDurNotes: boolean;
    enableRTPB: boolean;
    patientDocumentTags: string[];
    saveTransferDocumentToPatientDocuments: boolean;
    hideIncompleteConsultationsOnPrintQueue: boolean;
    hideIncompleteAnswersOnPrintQueue: boolean;
    transferReasons: any[];
    patientAssistanceProgramRejectReasons: string[];
    priorAuthStatusReasons: PriorAuthStatusReason[];
    priorAuthNextTouchDays: PriorAuthNextTouch[];
    priorAuthNoteTemplates: string[];
    doctorFollowUpNextTouchDays: DoctorFollowUpNextTouch[];
    doctorFollowUpMaxDays: number;
    papNextTouchDays: any[];
    faxRoutingRules: FaxRoutingRule[];
    planTypeCanBeUnknown: boolean;
    caseProblems: string[];
    npcTextHours: number;
    awaitingInsuranceHours: number;
    outOfNetworkHours: number;
    initialRefillDays: number[];
    deliveryConfirmationDays: number[];
    pharmacistOnlyNotes: boolean;
    excludeRefillRequestsFromNotes: boolean;
    // awaitingInsuranceVoicemailDays: number; Obsolete, use new auto outbound config
    createTaskOnMPPPClaimFromTaskTemplateId: number | null;
    createTaskOnMPPPEligibilityFromTaskTemplateId: number | null;
    autoRefillNextProcessDateDefaultCadence: number | null;
    autoRefillNextProcessDateDefaultCadenceType: number | null;
    autoRefillCopayDifferenceAllowance: number;
    autoRefillAutoProcessAuditChance: number;
    dispenseErrorLabel: string;
    prescriberFollowUpNoteTemplates: string[];
    specialties: Speciality[];
    allowShippingControlledWithoutPdmp: boolean;
    autoOutboundStatusConfigurations: AutoOutboundStatusConfiguration[];
    autoOutboundPartialPaymentConfiguration: AutoOutboundConfiguration;
    autoOutboundDeliveryConfirmationConfiguration: AutoOutboundConfiguration;
    providerFollowUpResponseTeamId: number | null;
    voicemailNotificationTeamId: number | null;
    parseMiddleNameFromEscript: boolean;
    rxTransferDeniedSubStatusId?: number | null;
    rxTransferDeniedTaskTemplateId?: number | null;
    providerPortalAccountCreationTaskTemplateId?: number | null;
    disableFutureWrittenDates: boolean;
    entityAlertConfigurations: EntityAlertConfigurations;
    faxTemplateOverrides?: Record<string, FaxTemplateOverride>;
    requirePharmacistOnExternalTransfers: boolean;
    useLastForDiffCheck: boolean;
    allowMultipleTaskAssignments: boolean;
}

export class Configuration {
    constructor(obj?: Configuration) {
        this.refillTextDays = 0;
        this.oldScriptRefillTextDays = 0;
        this.ordersAllowPV1Prescriptions = false;
        this.requireExplicitVerificationPV1 = false;
        this.requireExplicitVerificationPV2 = false;
        this.hideDispensingFields = false;
        this.autoRefillPromptStatement = "";
        this.autoRefillPromptStatementSpanish = "";
        this.autoRefillPromptStatementRussian = "";
        this.autoRefillPromptStatementSomali = "";
        this.autoRefillPromptStatementArabic = "";
        this.autoRefillPromptStatementChineseSimplified = "";
        this.autoRefillPromptStatementVietnamese = "";
        this.autoRefillPromptStatementFarsi = "";
        this.autoRefillPromptStatementKorean = "";
        this.autoRefillPromptStatementRomanian = "";
        this.autoRefillPromptStatementSwahili = "";
        this.autoRefillPromptStatementBurmese = "";
        this.autoRefillPromptStatementNepali = "";
        this.autoRefillPromptStatementAmharic = "";
        this.autoRefillPromptStatementPashto = "";
        this.autoCaseAppNewPatientCreation = false;
        this.autoCaseTextsNoValidNumbers = false;
        this.autoCaseTextsPatientContactFailure = false;
        this.autoCaseTextsFailAutoRefill = false;
        this.partialPaymentTextThresholdAmount = 0;
        this.npcTextThresholdAmount = 0;
        this.infinitrakApiKey = "";
        this.disableMedGuidePrinting = false;
        this.textMessageTemplates = new TextMessageTemplates();
        this.emailTemplates = new EmailTemplates();
        this.readyForPickupTexts = false;
        this.disableWebNotifications = false;
        this.requireLotsOnInventoryBatches = false;
        this.blockDeactivatingDrugIfInventoryOnHand = false;
        this.inventoryBatchSubmitEndpoint = "";
        this.allowAutorefillWithoutNotification = false;
        this.allowAutorefillWithoutPayment = false;
        this.faxCoverPage = false;
        this.patientAppCheckoutAttestation = "";
        this.requireExplicitDur = null;
        this.requireDurNotes = false;
        this.enableRTPB = false;
        this.saveTransferDocumentToPatientDocuments = true;
        this.hideIncompleteConsultationsOnPrintQueue = false;
        this.doctorFollowUpMaxDays = 7;
        this.planTypeCanBeUnknown = false;
        this.npcTextHours = 24;
        this.awaitingInsuranceHours = 24;
        this.outOfNetworkHours = 24;
        this.pharmacistOnlyNotes = true;
        this.excludeRefillRequestsFromNotes = false;
        this.createTaskOnMPPPClaimFromTaskTemplateId = null;
        this.createTaskOnMPPPEligibilityFromTaskTemplateId = null;
        this.autoRefillCopayDifferenceAllowance = 0;
        this.autoRefillAutoProcessAuditChance = 0;
        this.allowShippingControlledWithoutPdmp = false;
        this.parseMiddleNameFromEscript = true;
        this.rxTransferDeniedSubStatusId = null;
        this.rxTransferDeniedTaskTemplateId = null;
        this.providerPortalAccountCreationTaskTemplateId = null;
        this.voicemailNotificationTeamId = null;
        this.disableFutureWrittenDates = false;
        this.autoOutboundPartialPaymentConfiguration = new AutoOutboundConfiguration();
        this.autoOutboundDeliveryConfirmationConfiguration = new AutoOutboundConfiguration();
        this.entityAlertConfigurations = {};
        this.faxTemplateOverrides = {};
        this.requirePharmacistOnExternalTransfers = false;
        this.useLastForDiffCheck = false;
        this.allowMultipleTaskAssignments = false;

        if (obj) {
            Object.assign(this, obj);
            if (obj.autoOutboundStatusConfigurations) {
                this.autoOutboundStatusConfigurations = obj.autoOutboundStatusConfigurations.map(x => new AutoOutboundStatusConfiguration(x));
            }
        }

        if (!this.faxTemplateOverrides) {
            this.faxTemplateOverrides = {};
        }

        if (!this.autoRefillNextProcessDateDefaultCadenceType) {
            this.autoRefillNextProcessDateDefaultCadenceType = 0;
        }

        if (!this.initialRefillDays) {
            this.initialRefillDays = [7];
        }
        if (!this.deliveryConfirmationDays) {
            this.deliveryConfirmationDays = [1, 7];
        }

        if (!this.inventoryReasons) {
            this.inventoryReasons = [
                "New Inventory",
                "Stock Check",
            ];
        }

        if (!this.textMessageTemplates) {
            this.textMessageTemplates = new TextMessageTemplates();
        }
        if (!this.emailTemplates) {
            this.emailTemplates = new EmailTemplates();
        }

        if (!this.orderCancellationReasons) {
            this.orderCancellationReasons = [];
        }

        if (!this.onHoldSubStatuses) {
            this.onHoldSubStatuses = [];
        }

        if (!this.discontinuedSubStatuses) {
            this.discontinuedSubStatuses = [];
        }

        if (!this.patientDocumentTags) {
            this.patientDocumentTags = [];
        }

        if (!this.transferReasons) {
            this.transferReasons = [];
        }

        if (!this.patientAssistanceProgramRejectReasons) {
            this.patientAssistanceProgramRejectReasons = [];
        }

        if (!this.priorAuthStatusReasons) {
            this.priorAuthStatusReasons = [];
        }

        if (!this.priorAuthNextTouchDays) {
            this.priorAuthNextTouchDays = [];
        }

        if (!this.priorAuthNoteTemplates) {
            this.priorAuthNoteTemplates = [];
        }

        if (!this.papNextTouchDays) {
            this.papNextTouchDays = [];
        }

        if (!this.caseProblems) {
            this.caseProblems = [];
        }

        if (!this.prescriberFollowUpNoteTemplates) {
            this.prescriberFollowUpNoteTemplates = [];
        }

        if (!this.specialties) {
            this.specialties = [];
        }

        if (!this.autoOutboundStatusConfigurations) {
            this.autoOutboundStatusConfigurations = [];
        }
        if (!this.autoOutboundPartialPaymentConfiguration) {
            this.autoOutboundPartialPaymentConfiguration = new AutoOutboundConfiguration();
        }
        if (!this.autoOutboundDeliveryConfirmationConfiguration) {
            this.autoOutboundDeliveryConfirmationConfiguration = new AutoOutboundConfiguration();
        }
    }

    toString(): string {
        return JSON.stringify(this);
    }

    typeOf(name: string): string {
        // using a default populated Configuration object here, so that when we change the values in the inputs,
        // it doesn't think that the type has changed. (e.g., number type going blank will then switch to the string type)
        const conf = new Configuration();
        type configType = keyof typeof conf;
        const val = conf[name as configType];
        const valType = typeof val;

        if (valType != "object") return valType;
        if ((val as any) instanceof Date) return 'Date';

        return valType;
    }
}

export interface CadenceFields {
    title: string;
    groups: string[];
    type: string;
    help: string | undefined,
    options: { value: number; text: string; }[] | undefined;
    append: string | undefined;
}

export function autoRefillCadenceFields(cadenceType: number): {
    autoRefillNextProcessDateDefaultCadence: CadenceFields;
    autoRefillNextProcessDateDefaultCadenceType: CadenceFields;
} {
    return {
        autoRefillNextProcessDateDefaultCadence: {
            title: "Auto Refill Next Process Date Default Cadence",
            groups: ["Auto Refill"],
            type: "number",
            help: cadenceType === 0 ?
                "Sets the percentage of days supply used for the next process date on auto refills by default.\n\nEnter a whole number percentage from 1-100." :
                "Sets the number of days before days supply to use for the next process date on auto refills by default.\n\nEnter a whole number.",
            options: undefined,
            append: cadenceType === 0 ? "%" : undefined,
        },
        autoRefillNextProcessDateDefaultCadenceType: {
            title: "Auto Refill Next Process Date Default Cadence Type",
            groups: ["Auto Refill"],
            type: "select",
            help: undefined,
            options: [
                {
                    value: 0,
                    text: "Percent of Days Supply",
                },
                {
                    value: 1,
                    text: "Days Before",
                },
            ],
            append: undefined,
        },
    };
}
